import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Plus, Wallet, AlertTriangle } from "lucide-react";

// Packages and HMO authorizations.
//
// A self-paid package of ten sessions and an HMO letter of authorization for ten
// are the same object: an authorized balance a session draws down. Modelling
// them once is why HMO cover costs almost nothing to support.

const STATE_STYLE = {
  active:         "bg-emerald-50 text-emerald-700 border-emerald-200",
  exhausted:      "bg-slate-50 text-slate-600 border-slate-200",
  expired:        "bg-slate-50 text-slate-500 border-slate-200",
  expired_unused: "bg-rose-50 text-rose-700 border-rose-200",
};
const STATE_LABEL = {
  active: "active",
  exhausted: "fully used",
  expired: "expired",
  expired_unused: "expired — sessions unused",
};

export default function PhysioBalances() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [denied, setDenied] = useState(false);
  const [failed, setFailed] = useState("");

  const load = () => {
    setLoading(true);
    setDenied(false);
    setFailed("");
    api.get("/physio/balances?limit=2000")
      .then(({ data }) => setRows(data.balances || []))
      .catch((e) => {
        // A refusal is NOT an empty result. Rendering a 403 as "Nothing
        // matches" tells a therapist this clinic has no packages on file,
        // which is a different and much worse statement than "not your screen".
        if (e?.response?.status === 403) setDenied(true);
        else {
          setFailed(formatErr(e?.response?.data?.detail));
          toast.error(formatErr(e?.response?.data?.detail));
        }
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter && r.state !== filter) return false;
      if (!t) return true;
      return (r.patient_name || "").toLowerCase().includes(t) ||
             (r.loa_number || "").toLowerCase().includes(t) ||
             (r.hmo_name || "").toLowerCase().includes(t);
    });
  }, [rows, filter, q]);

  const counts = useMemo(() => {
    const c = {};
    rows.forEach((r) => { c[r.state] = (c[r.state] || 0) + 1; });
    return c;
  }, [rows]);

  const lapsedSessions = rows
    .filter((r) => r.state === "expired_unused")
    .reduce((s, r) => s + (r.remaining || 0), 0);

  if (denied) {
    return (
      <div className="p-4 md:p-6" data-testid="physio-balances-denied">
        <Card className="p-6 max-w-xl">
          <div className="flex items-center gap-2 font-bold text-slate-900">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Packages &amp; authorizations
          </div>
          <p className="text-sm text-slate-600 mt-2">
            This screen belongs to the front desk. Reception, the cashier and clinic
            admins can see what each patient is funded for.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            You are signed in with a role that cannot read billing. This is a
            permission boundary &mdash; it does <span className="font-semibold">not</span> mean
            there are no packages on file.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="physio-balances">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Work Sans" }}>
            Packages &amp; authorizations
          </h1>
          <p className="text-sm text-slate-500">What each patient is funded for.</p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" />New package / LOA
        </Button>
      </div>

      {lapsedSessions > 0 && (
        <Card className="p-3 bg-rose-50 border-rose-200 flex items-center gap-2 text-sm text-rose-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            <span className="font-semibold">{lapsedSessions} paid or authorized sessions</span> have
            lapsed unused across {counts.expired_unused} patients. After the expiry date that is
            the clinic&rsquo;s loss, not the patient&rsquo;s.
          </span>
        </Card>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        {[["", "All"], ["active", "Active"], ["expired_unused", "Lapsed unused"],
          ["exhausted", "Fully used"], ["expired", "Expired"]].map(([k, l]) => (
          <Button key={k || "all"} size="sm" variant={filter === k ? "default" : "outline"}
                  onClick={() => setFilter(k)}>
            {l}{k && counts[k] ? ` (${counts[k]})` : ""}
          </Button>
        ))}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Patient, HMO or LOA number…"
                 value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? <div className="p-6 text-slate-500">Loading…</div> : failed ? (
          <div className="p-6 text-sm text-rose-700">
            <div className="font-semibold">Could not load balances.</div>
            <div className="mt-1">{failed}</div>
            <div className="mt-1 text-slate-500">
              This is a failure to read, not an empty list.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-semibold px-4 py-2">Patient</th>
                  <th className="text-left font-semibold px-4 py-2">Funded by</th>
                  <th className="text-right font-semibold px-4 py-2">Used</th>
                  <th className="text-right font-semibold px-4 py-2">Left</th>
                  <th className="text-left font-semibold px-4 py-2">Valid until</th>
                  <th className="text-left font-semibold px-4 py-2">State</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium text-slate-900 whitespace-nowrap">
                      {r.patient_name || "—"}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {r.funding_source === "hmo" ? (
                        <span>
                          <span className="font-medium">{r.hmo_name}</span>
                          <span className="text-xs text-slate-500 ml-1.5">{r.loa_number}</span>
                        </span>
                      ) : (
                        <span className="text-slate-600">
                          Self-paid
                          {r.amount ? <span className="text-xs text-slate-400 ml-1.5">
                            ₱{Number(r.amount).toLocaleString()}</span> : null}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {r.consumed} / {r.authorized}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">
                      {r.remaining}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-slate-600">
                      {(r.valid_until || "").slice(0, 10) || "—"}
                      {r.days_to_expiry != null && r.days_to_expiry <= 14 && (
                        <span className="ml-1.5 text-xs font-semibold text-amber-700">
                          {r.days_to_expiry}d
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATE_STYLE[r.state] || ""}`}>
                        {STATE_LABEL[r.state] || r.state}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="outline"
                              onClick={() => nav(`/physio/patients/${r.patient_id}`)}>Chart</Button>
                    </td>
                  </tr>
                ))}
                {shown.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-6 text-slate-500">Nothing matches.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <NewBalanceDialog open={open} onOpenChange={setOpen} onDone={load} />
    </div>
  );
}

function NewBalanceDialog({ open, onOpenChange, onDone }) {
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [source, setSource] = useState("self_pay");
  const [authorized, setAuthorized] = useState(10);
  const [amount, setAmount] = useState(6300);
  const [hmo, setHmo] = useState("");
  const [loa, setLoa] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/patients")
      .then(({ data }) => setPatients(Array.isArray(data) ? data : data.patients || []))
      .catch(() => setPatients([]));
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/physio/balances", {
        patient_id: patientId,
        funding_source: source,
        authorized: Number(authorized),
        amount: source === "self_pay" ? Number(amount) : 0,
        hmo_name: source === "hmo" ? hmo : "",
        loa_number: source === "hmo" ? loa : "",
        valid_until: validUntil ? new Date(validUntil).toISOString() : "",
      });
      toast.success("Recorded");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle><Wallet className="h-4 w-4 inline mr-1.5" />New package or authorization</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium mb-1">Patient</div>
            <select className="w-full border rounded-md text-sm px-2 py-2"
                    value={patientId} onChange={(e) => setPatientId(e.target.value)}>
              <option value="">— choose —</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant={source === "self_pay" ? "default" : "outline"}
                    onClick={() => setSource("self_pay")}>Self-paid package</Button>
            <Button size="sm" variant={source === "hmo" ? "default" : "outline"}
                    onClick={() => setSource("hmo")}>HMO authorization</Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-sm font-medium mb-1">Sessions authorized</div>
              <Input type="number" value={authorized} onChange={(e) => setAuthorized(e.target.value)} />
            </div>
            {source === "self_pay" ? (
              <div>
                <div className="text-sm font-medium mb-1">Amount paid (₱)</div>
                <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
            ) : (
              <div>
                <div className="text-sm font-medium mb-1">HMO</div>
                <Input value={hmo} onChange={(e) => setHmo(e.target.value)} placeholder="Maxicare" />
              </div>
            )}
          </div>
          {source === "hmo" && (
            <div>
              <div className="text-sm font-medium mb-1">
                LOA number <span className="text-rose-600">*</span>
              </div>
              <Input value={loa} onChange={(e) => setLoa(e.target.value)} placeholder="LOA-MAX-01234" />
              <p className="text-xs text-slate-500 mt-1">
                Required — without it the sessions cannot be claimed, and the clinic
                finds out after they have been delivered.
              </p>
            </div>
          )}
          <div>
            <div className="text-sm font-medium mb-1">
              Valid until {source === "hmo" && <span className="text-rose-600">*</span>}
            </div>
            <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !patientId}>
            {saving ? "Saving…" : "Record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
